import { useTranslation } from 'react-i18next';

import { SUPPORT } from 'business/constants';
import { UserInfoT } from 'business/definitions';
import { joinArrayOfStrings } from 'helpers/helpers';
import validators from 'helpers/validators';
import { UserCreationFormState } from '../CreateUserContext/types';
import { isEmailDomainSupportWhitelisted } from '../helpers/email';

const userFormKeysToComplete = ['gender', 'firstname', 'lastname', 'job'];

function checkEmailAvailabilityIgnoreCase(email: string, usersEmail: string[]) {
    const lowerCasedEmail = email.toLowerCase();
    const isEmailAlreadyUsed = usersEmail.some(
        (item) => item.toLowerCase() === lowerCasedEmail,
    );
    return isEmailAlreadyUsed;
}

/**
 * Hook to validate the user personal information for the creation and update of a user.
 * @param  {UserInfoT} userData - Current values of the user information form
 * @param  {string[]} existingUsersEmails - List of the existing emails
 * NB: The existingUsersEmails does not contain the one being modified in case of an update.
 */
export const useValidateUserPersonalInformation = (
    userData: UserCreationFormState,
    existingUsersEmails: string[],
): string | undefined => {
    const { t } = useTranslation();

    const incompleteUserFields: string[] = [];
    userFormKeysToComplete.forEach((key: keyof UserInfoT) => {
        if (!userData[key]) incompleteUserFields.push(t(`common.${key}`));
    });
    if (incompleteUserFields.length !== 0)
        return t('userFormValidation.missingFields', {
            missingFields: joinArrayOfStrings(incompleteUserFields, t),
        });

    const { email } = userData;
    if (!email || !validators.isEmail(email)) {
        return t('userFormValidation.incorrectEmail');
    }

    return checkEmailAvailabilityIgnoreCase(email, existingUsersEmails)
        ? t('userFormValidation.emailAlreadyUsed')
        : undefined;
};

/**
 * Hook to check that the support role is allowed for the user.
 * @param  {UserInfoT} userData - Current values of the user information form
 */
export const useValidateUserSupportRole = (
    userData: UserCreationFormState,
): string | undefined => {
    const { t } = useTranslation();

    const { email, roles } = userData;
    if (roles.includes(SUPPORT) && !isEmailDomainSupportWhitelisted(email)) {
        return t('userFormValidation.incorrectDomainForSupportRole');
    }

    return undefined;
};

/**
 * Hook to validate the user information for the creation and update of a user.
 * @param  {UserInfoT} userData - Current values of the user information form
 * @param  {string[]} existingUsersEmails - List of the existing emails
 */
export const useValidateUserForm = (
    userData: UserCreationFormState,
    existingUsersEmails: string[],
): string | undefined => {
    const personalInformationWarning = useValidateUserPersonalInformation(
        userData,
        existingUsersEmails,
    );
    const supportRoleWarning = useValidateUserSupportRole(userData);
    return personalInformationWarning || supportRoleWarning;
};
